import {route} from 'ziggy-js';
import {useToast} from 'primevue';
import {ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {usePage} from '@inertiajs/vue3';
import {useImageStore} from '@/Stores/ImageStore';
import {useSceneStore} from '@/Stores/SceneStore';
import i18n from '@/Translations/i18n';

function hasCanvasDesignContent() {
    return useImageStore().hasDesignContent;
}

function getTeamSession() {
    return window?.Inertia?.page?.props?._tsr ?? {};
}

function translate(key) {
    return i18n.global.t(key);
}

async function saveDesignBeforeRender() {
    const {useImageStore} = await import('@/Stores/ImageStore');
    const imageStore = useImageStore();
    const sceneStore = useSceneStore();

    if (imageStore.canvas) {
        const {SaveImage} = await import('@/Components/editor/actions/SaveImage');
        await new SaveImage(imageStore, sceneStore).execute();
    }
}

export async function previewGenerateAssetsNow(options = {}) {
    const teamSession = getTeamSession();
    const force = Boolean(options.force);
    const skipSave = Boolean(options.skipSave);

    if (teamSession.direct_render_enabled || !hasCanvasDesignContent()) {
        return;
    }

    try {
        if (!skipSave) {
            const {flushPendingDesignSave} = await import('@/Composables/useDesignSyncSchedule.js');
            const flushed = await flushPendingDesignSave();

            if (!flushed) {
                await saveDesignBeforeRender();
            }
        }

        const url = route('configurator.api.image.generate-assets-preview');
        await axios.post(url, null, {
            params: force ? {force: 1} : {},
        });
    } catch (error) {
        const errorMessage = error.response?.data?.message || translate('errors.asset_generation_failed');
        console.log(errorMessage);
    }
}

export async function finalGenerateAssetsNow() {
    const teamSession = getTeamSession();

    if (teamSession.direct_render_enabled || !hasCanvasDesignContent()) {
        return;
    }

    try {
        await saveDesignBeforeRender();

        const url = route('configurator.api.image.generate-assets-final');
        await axios.post(url);
    } catch (error) {
        const errorMessage = error.response?.data?.message || translate('errors.asset_generation_failed');
        console.log(errorMessage);
    }
}

export function warmupGenerateAssetsNow() {
    const teamSession = getTeamSession();

    if (teamSession.direct_render_enabled || !hasCanvasDesignContent()) {
        return;
    }

    const url = route('configurator.api.image.generate-assets-warmup');
    axios.post(url).catch((error) => {
        const errorMessage = error.response?.data?.message || translate('errors.asset_generation_failed');
        console.log(errorMessage);
    });
}

export function useGenerateAssets() {
    const toast = useToast();
    const {t} = useI18n();
    const page = usePage();
    const sceneStore = useSceneStore();
    const loadingAssets = ref(false);

    async function generateAssetsNow() {
        const teamSession = page.props._tsr || {};
        loadingAssets.value = true;

        try {
            if (teamSession.direct_render_enabled) {
                await sceneStore.saveGeneratedImages({
                    count: Number(teamSession.direct_render_image_count || 4),
                    transparent: Boolean(teamSession.transparent_background),
                });
            } else {
                await previewGenerateAssetsNow({force: true});
            }

            toast.add({
                severity: 'success',
                summary: t('messages.success'),
                detail: t('messages.assets_generated'),
                life: 3000,
            });

            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || t('errors.asset_generation_failed');
            toast.add({
                severity: 'error',
                summary: t('messages.error'),
                detail: errorMessage,
                life: 5000,
            });

            return false;
        } finally {
            loadingAssets.value = false;
        }
    }

    return {
        generateAssetsNow,
        previewGenerateAssetsNow,
        finalGenerateAssetsNow,
        warmupGenerateAssetsNow,
        loadingAssets,
    };
}

/** @deprecated Use useGenerateAssets() inside setup, or standalone exports for background work. */
export function generateAssets() {
    return useGenerateAssets();
}